export interface AgeValidationResult {
  isValid: boolean;
  age: number | null;
  reason: 'underage' | 'invalid_date' | 'future_date' | null;
}

export function validateAge(
  birthDate: string,
  referenceDate: Date = new Date(),
): AgeValidationResult {
  if (!birthDate) return { isValid: false, age: null, reason: 'invalid_date' };

  let date: Date;
  if (birthDate.includes('/')) {
    const [day, month, year] = birthDate.split('/');
    date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  } else {
    date = new Date(birthDate);
  }

  if (isNaN(date.getTime())) return { isValid: false, age: null, reason: 'invalid_date' };
  if (date > new Date()) return { isValid: false, age: null, reason: 'future_date' };

  let age = referenceDate.getFullYear() - date.getFullYear();
  const m = referenceDate.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && referenceDate.getDate() < date.getDate())) age--;

  if (age < 18) return { isValid: false, age, reason: 'underage' };
  return { isValid: true, age, reason: null };
}

export function getAgeValidationMessage(reason: AgeValidationResult['reason']): string {
  switch (reason) {
    case 'underage':
      return 'O Professya é destinado a maiores de 18 anos. Se você acredita que houve um erro, entre em contato pelo suporte@professya.com';
    case 'future_date':
      return 'A data de nascimento não pode ser uma data futura.';
    case 'invalid_date':
      return 'Data de nascimento inválida. Verifique o formato.';
    default:
      return '';
  }
}

export async function logAgeValidation(opts: {
  userId: string | null;
  birthDate: string;
  age: number | null;
  result: 'passed' | 'blocked_underage' | 'blocked_invalid_date' | 'blocked_future_date' | 'blocked_fraud';
  context: 'registration' | 'profile_update' | 'natal_chart_setup' | 'natal_chart_update';
  notes?: string;
}) {
  try {
    const { supabase } = await import('../lib/supabase');
    // Parse birth_date to date format
    let dateStr = opts.birthDate;
    if (dateStr.includes('/')) {
      const [d, m, y] = dateStr.split('/');
      dateStr = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    await supabase.from('age_validation_log').insert({
      user_id: opts.userId,
      birth_date_attempted: dateStr,
      age_calculated: opts.age,
      result: opts.result,
      context: opts.context,
      notes: opts.notes ?? null,
    });
  } catch {
    // Fire-and-forget, never block the user flow
  }
}

export async function logConsent(opts: {
  userId: string;
  consentType: 'marketing_email' | 'platform_alerts' | 'sensitive_data' | 'analytics_cookies' | 'service_generation' | 'terms_of_use' | 'cancellation_policy' | 'privacy_policy';
  action: 'granted' | 'revoked';
  context?: string;
  serviceSlug?: string;
}) {
  try {
    const { supabase } = await import('../lib/supabase');
    await supabase.from('consent_log').insert({
      user_id: opts.userId,
      consent_type: opts.consentType,
      action: opts.action,
      context: opts.context ?? null,
      service_slug: opts.serviceSlug ?? null,
      user_agent: navigator.userAgent,
    });
  } catch {
    // Fire-and-forget
  }
}
